import React, { useState } from 'react';

const CreditPackages = ({ packages, editingPackage, setEditingPackage, showAddPackage, setShowAddPackage, onCreate, onUpdate, onDelete, colorTheme }) => {
  const [formData, setFormData] = useState({
    name: '',
    credits: 0,
    price_usd: 0,
    discount_percentage: 0,
    badge_text: '',
    display_order: 0,
    is_active: true
  });

  const handleEdit = (pkg) => {
    setEditingPackage(pkg.id);
    setFormData(pkg);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingPackage) {
      onUpdate(editingPackage, formData);
    } else {
      onCreate(formData);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      credits: 0,
      price_usd: 0,
      discount_percentage: 0,
      badge_text: '',
      display_order: 0,
      is_active: true
    });
    setEditingPackage(null);
    setShowAddPackage(false);
  };

  const getThemeClasses = () => {
    if (colorTheme === 'purple') {
      return {
        button: 'bg-purple-600 hover:bg-purple-700 text-white',
        badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
        border: 'border-purple-200 dark:border-purple-700'
      };
    } else if (colorTheme === 'blue') {
      return {
        button: 'bg-blue-600 hover:bg-blue-700 text-white',
        badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        border: 'border-blue-200 dark:border-blue-700'
      };
    }
    return {
      button: 'bg-green-600 hover:bg-green-700 text-white',
      badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      border: 'border-green-200 dark:border-green-700'
    };
  };

  const theme = getThemeClasses();

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Credit Packages</h2>
        {!showAddPackage && !editingPackage && (
          <button
            onClick={() => setShowAddPackage(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${theme.button}`}
          >
            + Add Package
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {(showAddPackage || editingPackage) && (
        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {editingPackage ? 'Edit Package' : 'Create New Package'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Package Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Starter Pack"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Credits *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.credits}
                  onChange={(e) => setFormData({...formData, credits: parseInt(e.target.value)})}
                  placeholder="e.g., 100"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price (USD) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.price_usd}
                  onChange={(e) => setFormData({...formData, price_usd: parseFloat(e.target.value)})}
                  placeholder="e.g., 20.00"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Discount %
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({...formData, discount_percentage: parseInt(e.target.value)})}
                  placeholder="e.g., 17"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Badge Text (Optional)
                </label>
                <input
                  type="text"
                  value={formData.badge_text}
                  onChange={(e) => setFormData({...formData, badge_text: e.target.value})}
                  placeholder="e.g., ⭐ Popular or 💎 Best Value"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.display_order}
                  onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value)})}
                  placeholder="e.g., 1, 2, 3"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Active (visible to users)
              </label>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-6 py-2 rounded-lg font-medium transition-colors ${theme.button}`}
              >
                {editingPackage ? 'Update Package' : 'Create Package'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Packages Grid */}
      {packages.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          No credit packages configured yet. Click "Add Package" to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-lg border-2 ${
                pkg.is_active ? theme.border : 'border-gray-300 dark:border-gray-600 opacity-60'
              } p-6 transition-all hover:shadow-xl`}
            >
              {/* Badge */}
              {pkg.badge_text && (
                <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${theme.badge}`}>
                  {pkg.badge_text}
                </div>
              )}

              {/* Package Info */}
              <div className="mb-4">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {pkg.name}
                </h3>
                <div className="flex items-baseline mb-2">
                  <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
                    ${pkg.price_usd}
                  </span>
                  <span className="ml-2 text-gray-500 dark:text-gray-400">USD</span>
                </div>
                <div className="text-lg text-gray-600 dark:text-gray-300 mb-2">
                  <span className="font-semibold">{pkg.credits}</span> Credits
                </div>
                {pkg.discount_percentage > 0 && (
                  <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                    {pkg.discount_percentage}% Discount
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  pkg.is_active 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {pkg.is_active ? '✓ Active' : '✕ Inactive'}
                </span>
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                  Order: {pkg.display_order}
                </span>
              </div>

              {/* Calculations */}
              <div className="mb-4 text-sm space-y-1">
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Cost per credit:</span>
                  <span className="font-medium">${(pkg.price_usd / pkg.credits).toFixed(3)}</span>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Text ads (~3 credits):</span>
                  <span className="font-medium">~{Math.floor(pkg.credits / 3)}</span>
                </div>
                <div className="flex justify-between text-gray-600 dark:text-gray-400">
                  <span>Complete campaigns (~65 credits):</span>
                  <span className="font-medium">~{Math.floor(pkg.credits / 65)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(pkg)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => onDelete(pkg.id)}
                  className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreditPackages;
